import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Button,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Label,
  Switch
} from '@fluentui/react-components';
import {
  Add20Regular,
  Mail20Regular,
  Edit20Regular,
  Delete20Regular,
  ToggleLeft20Regular,
  ToggleRight20Regular,
  Dismiss20Regular,
  Save20Regular,
  Settings20Regular,
  Search20Regular
} from '@fluentui/react-icons';
import { alertsService } from '../../services/alertsService';
import type {
  AlertRecipientDto,
  CreateAlertRecipientRequest,
  UpdateAlertRecipientRequest,
  SeverityFilter
} from '../../types';

const useStyles = makeStyles({
  // Card resumo (sempre visível)
  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    paddingTop: '18px',
    paddingBottom: '18px',
    paddingLeft: '22px',
    paddingRight: '22px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: '12px',
  },
  summaryIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  summaryInfo: {
    flex: 1,
    minWidth: 0,
  },
  summaryTitle: {
    marginTop: 0,
    marginRight: 0,
    marginBottom: '4px',
    marginLeft: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  summaryStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  statItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: tokens.colorNeutralForeground2,
  },
  statStrong: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  pip: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  pipAll: { backgroundColor: tokens.colorNeutralForeground3 },
  pipWarning: { backgroundColor: tokens.colorPaletteYellowForeground1 },
  pipCritical: { backgroundColor: tokens.colorPaletteRedForeground1 },

  // Modal de gerenciamento (lista)
  manageDialogSurface: {
    maxWidth: '900px',
    width: '92vw',
  },
  manageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  manageToolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    paddingTop: '12px',
    paddingBottom: '16px',
  },
  searchInput: {
    flex: 1,
    maxWidth: '320px',
  },
  tableContainer: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: '8px',
    overflow: 'hidden',
  },
  emailCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    paddingRight: '12px',
  },
  truncateCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: '12px',
  },
  emptyState: {
    padding: '32px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  actionsCell: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'center',
  },
  statusIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  statusDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  statusDotActive: { backgroundColor: tokens.colorPaletteGreenForeground1 },
  statusDotInactive: { backgroundColor: tokens.colorNeutralForeground4 },
  statusInactive: { color: tokens.colorNeutralForeground3 },
  severityIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },

  // Sub-modal de form (adicionar/editar)
  formDialogSurface: {
    maxWidth: '520px',
    width: '92vw',
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  formHelper: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: '8px',
    cursor: 'pointer',
    transitionProperty: 'border-color, background-color',
    transitionDuration: '0.15s',
  },
  radioOptionSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  radioCircle: {
    width: '16px',
    height: '16px',
    ...shorthands.border('2px', 'solid', tokens.colorNeutralStroke1),
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '2px',
    position: 'relative',
  },
  radioCircleSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground,
  },
  radioLabel: {
    flex: 1,
  },
  radioTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  radioDesc: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: '2px',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: '8px',
  },
  dialogActionsRow: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
});

interface EmailRecipientsSectionProps {
  onChanged?: () => void;
}

const SEVERITY_DESCRIPTIONS: Record<SeverityFilter, { title: string; desc: string }> = {
  All: {
    title: 'Warning + Critical',
    desc: 'Recebe todos os alertas, independente da severidade.'
  },
  Warning: {
    title: 'Apenas Warning',
    desc: 'Recebe somente quando observado ≥ limite (excluindo críticos).'
  },
  Critical: {
    title: 'Apenas Critical',
    desc: 'Recebe somente quando observado ≥ 2× o limite (situações graves).'
  }
};

export const EmailRecipientsSection: React.FC<EmailRecipientsSectionProps> = ({ onChanged }) => {
  const styles = useStyles();

  const [recipients, setRecipients] = useState<AlertRecipientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal de gerenciamento
  const [manageOpen, setManageOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Sub-modal de form
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<AlertRecipientDto | null>(null);
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formSeverity, setFormSeverity] = useState<SeverityFilter>('All');
  const [saving, setSaving] = useState(false);

  const loadRecipients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await alertsService.getRecipients();
      setRecipients(data);
    } catch (err) {
      console.error('Error loading recipients:', err);
      setError('Erro ao carregar destinatários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const stats = useMemo(() => {
    const active = recipients.filter(r => r.isActive);
    return {
      total: recipients.length,
      active: active.length,
      all: active.filter(r => r.severityFilter === 'All').length,
      warning: active.filter(r => r.severityFilter === 'Warning').length,
      critical: active.filter(r => r.severityFilter === 'Critical').length
    };
  }, [recipients]);

  const filteredRecipients = useMemo(() => {
    if (!searchTerm.trim()) return recipients;
    const term = searchTerm.toLowerCase();
    return recipients.filter(r =>
      r.email.toLowerCase().includes(term) ||
      r.name.toLowerCase().includes(term)
    );
  }, [recipients, searchTerm]);

  const openAddForm = () => {
    setEditingRecipient(null);
    setFormEmail('');
    setFormName('');
    setFormActive(true);
    setFormSeverity('All');
    setFormOpen(true);
  };

  const openEditForm = (recipient: AlertRecipientDto) => {
    setEditingRecipient(recipient);
    setFormEmail(recipient.email);
    setFormName(recipient.name);
    setFormActive(recipient.isActive);
    setFormSeverity(recipient.severityFilter);
    setFormOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (editingRecipient) {
        const request: UpdateAlertRecipientRequest = {
          name: formName,
          isActive: formActive,
          severityFilter: formSeverity
        };
        await alertsService.updateRecipient(editingRecipient.email, request);
      } else {
        const request: CreateAlertRecipientRequest = {
          email: formEmail,
          name: formName,
          isActive: formActive,
          severityFilter: formSeverity
        };
        await alertsService.createRecipient(request);
      }

      setFormOpen(false);
      await loadRecipients();
      onChanged?.();
    } catch (err) {
      console.error('Error saving recipient:', err);
      setError('Erro ao salvar destinatário');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (email: string) => {
    try {
      await alertsService.toggleRecipientStatus(email);
      await loadRecipients();
      onChanged?.();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Erro ao alterar status');
    }
  };

  const handleDelete = async (email: string) => {
    if (!confirm(`Deseja remover o destinatário ${email}?`)) return;
    try {
      await alertsService.deleteRecipient(email);
      await loadRecipients();
      onChanged?.();
    } catch (err) {
      console.error('Error deleting recipient:', err);
      setError('Erro ao remover destinatário');
    }
  };

  const renderSeverityChip = (filter: SeverityFilter) => {
    const pipClass = filter === 'All'
      ? styles.pipAll
      : filter === 'Warning' ? styles.pipWarning : styles.pipCritical;
    const text = filter === 'All' ? 'Warning + Critical'
      : filter === 'Warning' ? 'Apenas Warning'
      : 'Apenas Critical';
    return (
      <span className={styles.severityIndicator}>
        <span className={`${styles.pip} ${pipClass}`} />
        {text}
      </span>
    );
  };

  return (
    <>
      {/* Card resumo */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryIcon}>
          <Mail20Regular />
        </div>
        <div className={styles.summaryInfo}>
          <h2 className={styles.summaryTitle}>Destinatários</h2>
          <div className={styles.summaryStats}>
            {loading ? (
              <span>Carregando…</span>
            ) : recipients.length === 0 ? (
              <span>Nenhum destinatário cadastrado.</span>
            ) : (
              <>
                <span className={styles.statItem}>
                  <span className={styles.statStrong}>{stats.active}</span>
                  &nbsp;ativo{stats.active !== 1 ? 's' : ''}
                  {stats.total !== stats.active && (
                    <span style={{ color: tokens.colorNeutralForeground3 }}>
                      &nbsp;de {stats.total}
                    </span>
                  )}
                </span>
                {stats.all > 0 && (
                  <span className={styles.statItem}>
                    <span className={`${styles.pip} ${styles.pipAll}`} />
                    {stats.all} {stats.all === 1 ? 'recebe Warning + Critical' : 'recebem Warning + Critical'}
                  </span>
                )}
                {stats.warning > 0 && (
                  <span className={styles.statItem}>
                    <span className={`${styles.pip} ${styles.pipWarning}`} />
                    {stats.warning} {stats.warning === 1 ? 'só Warning' : 'só Warning'}
                  </span>
                )}
                {stats.critical > 0 && (
                  <span className={styles.statItem}>
                    <span className={`${styles.pip} ${styles.pipCritical}`} />
                    {stats.critical} {stats.critical === 1 ? 'só Critical' : 'só Critical'}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <Button
          appearance="primary"
          icon={<Settings20Regular />}
          onClick={() => setManageOpen(true)}
        >
          Gerenciar destinatários
        </Button>
      </div>

      {error && !manageOpen && !formOpen && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Modal de gerenciamento */}
      <Dialog open={manageOpen} onOpenChange={(_, data) => !data.open && setManageOpen(false)}>
        <DialogSurface className={styles.manageDialogSurface}>
          <DialogBody>
            <DialogTitle>
              <div className={styles.manageHeader}>
                <Mail20Regular />
                Gerenciar destinatários
              </div>
            </DialogTitle>
            <DialogContent>
              {error && (
                <MessageBar intent="error" style={{ marginBottom: '12px' }}>
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}

              <div className={styles.manageToolbar}>
                <Input
                  className={styles.searchInput}
                  contentBefore={<Search20Regular />}
                  placeholder="Buscar por e-mail ou nome..."
                  value={searchTerm}
                  onChange={(_, data) => setSearchTerm(data.value)}
                />
                <Button
                  appearance="primary"
                  icon={<Add20Regular />}
                  onClick={openAddForm}
                >
                  Adicionar destinatário
                </Button>
              </div>

              {loading ? (
                <Spinner size="medium" label="Carregando..." />
              ) : filteredRecipients.length === 0 ? (
                <div className={styles.emptyState}>
                  {searchTerm
                    ? 'Nenhum destinatário corresponde à busca.'
                    : 'Nenhum destinatário cadastrado. Clique em "Adicionar destinatário".'}
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <Table size="small" style={{ tableLayout: 'fixed' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell style={{ width: '32%' }}>E-mail</TableHeaderCell>
                        <TableHeaderCell style={{ width: '22%' }}>Nome</TableHeaderCell>
                        <TableHeaderCell style={{ width: '22%' }}>Severidade</TableHeaderCell>
                        <TableHeaderCell style={{ width: '12%' }}>Status</TableHeaderCell>
                        <TableHeaderCell style={{ width: '12%' }}>
                          <span style={{ display: 'block', textAlign: 'center' }}>Ações</span>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRecipients.map(recipient => (
                        <TableRow key={recipient.email}>
                          <TableCell className={styles.emailCell}>{recipient.email}</TableCell>
                          <TableCell className={styles.truncateCell}>{recipient.name}</TableCell>
                          <TableCell>{renderSeverityChip(recipient.severityFilter)}</TableCell>
                          <TableCell>
                            <span className={`${styles.statusIndicator} ${!recipient.isActive ? styles.statusInactive : ''}`}>
                              <span className={`${styles.statusDot} ${recipient.isActive ? styles.statusDotActive : styles.statusDotInactive}`} />
                              {recipient.isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className={styles.actionsCell}>
                              <Tooltip content="Editar" relationship="label">
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  icon={<Edit20Regular />}
                                  onClick={() => openEditForm(recipient)}
                                />
                              </Tooltip>
                              <Tooltip content={recipient.isActive ? 'Desativar' : 'Ativar'} relationship="label">
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  icon={recipient.isActive ? <ToggleRight20Regular /> : <ToggleLeft20Regular />}
                                  onClick={() => handleToggleStatus(recipient.email)}
                                />
                              </Tooltip>
                              <Tooltip content="Excluir" relationship="label">
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  icon={<Delete20Regular />}
                                  onClick={() => handleDelete(recipient.email)}
                                />
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                icon={<Dismiss20Regular />}
                onClick={() => setManageOpen(false)}
              >
                Fechar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Sub-modal: Adicionar/Editar */}
      <Dialog open={formOpen} onOpenChange={(_, data) => !data.open && setFormOpen(false)}>
        <DialogSurface className={styles.formDialogSurface}>
          <DialogBody>
            <DialogTitle>
              <div className={styles.formHeader}>
                {editingRecipient ? <Edit20Regular /> : <Add20Regular />}
                {editingRecipient ? 'Editar destinatário' : 'Adicionar destinatário'}
              </div>
            </DialogTitle>
            <DialogContent>
              <div className={styles.formField}>
                <Label required>E-mail</Label>
                <Input
                  type="email"
                  value={formEmail}
                  onChange={(_, data) => setFormEmail(data.value)}
                  disabled={!!editingRecipient}
                  placeholder="email@exemplo.com"
                />
                {editingRecipient && (
                  <span className={styles.formHelper}>
                    O e-mail não pode ser alterado. Para mudar, exclua e crie um novo destinatário.
                  </span>
                )}
              </div>

              <div className={styles.formField}>
                <Label required>Nome</Label>
                <Input
                  value={formName}
                  onChange={(_, data) => setFormName(data.value)}
                  placeholder="Nome completo"
                />
              </div>

              <div className={styles.formField}>
                <Label>Severidade dos alertas que este destinatário receberá</Label>
                <div className={styles.radioGroup}>
                  {(['All', 'Warning', 'Critical'] as SeverityFilter[]).map(value => {
                    const isSelected = formSeverity === value;
                    const info = SEVERITY_DESCRIPTIONS[value];
                    const pipClass = value === 'All'
                      ? styles.pipAll
                      : value === 'Warning' ? styles.pipWarning : styles.pipCritical;
                    return (
                      <div
                        key={value}
                        className={`${styles.radioOption} ${isSelected ? styles.radioOptionSelected : ''}`}
                        onClick={() => setFormSeverity(value)}
                      >
                        <span className={`${styles.radioCircle} ${isSelected ? styles.radioCircleSelected : ''}`} />
                        <div className={styles.radioLabel}>
                          <div className={styles.radioTitle}>
                            <span className={`${styles.pip} ${pipClass}`} />
                            {info.title}
                          </div>
                          <div className={styles.radioDesc}>{info.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.formField}>
                <div className={styles.toggleRow}>
                  <Switch
                    checked={formActive}
                    onChange={(_, data) => setFormActive(!!data.checked)}
                  />
                  <span>Ativo para receber alertas</span>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <div className={styles.dialogActionsRow}>
                <Button
                  appearance="secondary"
                  icon={<Dismiss20Regular />}
                  onClick={() => setFormOpen(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  appearance="primary"
                  icon={<Save20Regular />}
                  onClick={handleSave}
                  disabled={saving || !formEmail || !formName}
                >
                  {saving ? <Spinner size="tiny" /> : 'Salvar'}
                </Button>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
